import { createTheme, Theme } from '@mui/material/styles';

// Define your custom theme with elegant design elements
const theme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#55cbcd', // Elegant teal
      dark: '#3da3a5',
      light: '#a2e1db',
    },
    secondary: {
      main: '#fcb9aa', // Elegant coral
      dark: '#f97171',
      light: '#ffdbcc',
    },
    text: {
      primary: '#385a7c', // Deep blue-gray
      secondary: '#4a5f71',
    },
    background: {
      default: '#eef4fa', // Light blue-gray background
      paper: '#ffffff',
    },
    action: {
      disabled: '#808080',
      disabledBackground: '#e0e0e0',
    },
  },
  typography: {
    fontFamily: 'Poppins, sans-serif',
    h1: {
      fontFamily: 'Soria, serif',
      fontWeight: 600,
      color: '#385a7c',
    },
    h2: {
      fontFamily: 'Soria, serif',
      fontWeight: 600,
      color: '#385a7c',
    },
    h3: {
      fontFamily: 'Soria, serif',
      fontWeight: 500,
      color: '#385a7c',
    },
    h4: {
      fontFamily: 'Soria, serif',
      fontWeight: 500,
      color: '#385a7c',
    },
    h5: {
      fontFamily: 'Soria, serif',
      fontWeight: 500,
      color: '#385a7c',
    },
    h6: {
      fontFamily: 'Soria, serif',
      fontWeight: 500,
      color: '#385a7c',
    },
    body1: {
      fontFamily: 'Poppins, sans-serif',
      color: '#385a7c',
    },
    body2: {
      fontFamily: 'Poppins, sans-serif',
      color: '#4a5f71',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: 'linear-gradient(120deg, rgba(224, 242, 254, 0.5) 0%, rgba(226, 232, 240, 0.3) 100%), radial-gradient(circle at 10% 20%, rgba(162, 225, 219, 0.1) 0%, transparent 20%), radial-gradient(circle at 90% 80%, rgba(252, 185, 170, 0.1) 0%, transparent 20%), radial-gradient(circle at 50% 50%, rgba(85, 203, 205, 0.05) 0%, transparent 30%)',
          fontFamily: 'Poppins, sans-serif',
        },
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #3a4f61, #4a5f71)',
          boxShadow: '0 4px 12px rgba(56, 90, 124, 0.15)',
          borderRadius: '0 !important',
          borderTopLeftRadius: '0 !important',
          borderTopRightRadius: '0 !important',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 500,
          borderRadius: '50px',
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(85, 203, 205, 0.3)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(85, 203, 205, 0.4)',
          }
        },
        contained: {
          background: 'linear-gradient(135deg, #55cbcd, #3da3a5)',
          '&:hover': {
            background: 'linear-gradient(135deg, #3da3a5, #55cbcd)',
          }
        },
        outlined: {
          borderColor: '#55cbcd',
          color: '#55cbcd',
          '&:hover': {
            borderColor: '#3da3a5',
            backgroundColor: 'rgba(85, 203, 205, 0.1)',
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '50%',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'scale(1.05)',
          },
          '& .MuiSvgIcon-root': {
            width: '1.2em',
            height: '1.2em',
            maxWidth: '1.2em',
            maxHeight: '1.2em',
          }
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          // Ensure icons don't exceed their containers
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          flexShrink: 0,
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        }
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderRadius: '12px',
          overflow: 'auto',
        }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f5f4f0',
          color: '#385a7c',
          fontWeight: 500,
          fontSize: '0.9rem',
        },
        body: {
          color: '#385a7c',
          fontSize: '0.9rem',
          borderTop: '1px solid rgba(85, 203, 205, 0.1)',
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(162, 225, 219, 0.05)',
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 500,
          borderRadius: '20px',
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #55cbcd, #3da3a5)',
          boxShadow: '0 4px 12px rgba(85, 203, 205, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #3da3a5, #55cbcd)',
            transform: 'scale(1.05)',
            boxShadow: '0 6px 16px rgba(85, 203, 205, 0.4)',
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          boxShadow: '0 8px 20px rgba(56, 90, 124, 0.15)',
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'Soria, serif',
          fontWeight: 600,
          color: '#385a7c',
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#55cbcd',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#55cbcd',
            }
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#55cbcd',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#55cbcd',
          }
        }
      }
    }
  },
});

export default theme;